/**
 * RITUEL MATIN / SOIR — checklist que l'enfant suit pas à pas. Chaque étape cochée
 * déclenche un feedback positif immédiat (principe Barkley : récompense fréquente, pas
 * d'attente de la fin). JARVIS guide à la voix ; l'écran montre la progression.
 * Thémé auto (--kid-*). Éditable par Pat via le dashboard parent.
 */
import { useCallback, useEffect, useState } from "react";
import { playConfirmChime } from "./chime";

const CHEERS = ["Bravo !", "Nickel !", "Et une de plus !", "Trop fort !", "Impec, agent !", "Continue comme ça !"];

export default function Ritual({ apiBase, moment, onClose }: { apiBase: string; moment: "morning" | "evening"; onClose: () => void }) {
  const [items, setItems] = useState<string[]>([]);
  const [done, setDone] = useState<boolean[]>([]);
  const [cheer, setCheer] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${apiBase}/api/child/ritual?moment=${moment}`);
        const d = await r.json();
        const list: string[] = Array.isArray(d.items) ? d.items : [];
        setItems(list);
        setDone(new Array(list.length).fill(false));
      } catch { /* offline */ }
    })();
  }, [apiBase, moment]);

  const total = items.length;
  const doneCount = done.filter(Boolean).length;
  const allDone = total > 0 && doneCount === total;

  const toggle = useCallback((i: number) => {
    setDone((prev) => {
      if (prev[i]) return prev; // on ne décoche pas (on avance)
      const next = prev.slice();
      next[i] = true;
      return next;
    });
    try { playConfirmChime(); } catch {}
    try { (navigator as any).vibrate?.(60); } catch {}
    setCheer(CHEERS[(Math.random() * CHEERS.length) | 0]);
    setTimeout(() => setCheer(""), 1400);
  }, []);

  return (
    <div style={S.screen}>
      <div style={S.header}>
        <button style={S.close} onClick={onClose} aria-label="Fermer">✕</button>
        <div style={S.title}>{moment === "morning" ? "☀️ RITUEL DU MATIN" : "🌙 RITUEL DU SOIR"}</div>
        <div style={S.count}>{doneCount}/{total}</div>
      </div>

      <div style={S.barTrack}><div style={{ ...S.barFill, width: total ? `${(doneCount / total) * 100}%` : "0%" }} /></div>

      <div style={S.scroll}>
        {items.map((it, i) => (
          <button key={i} onClick={() => toggle(i)} style={{ ...S.item, ...(done[i] ? S.itemDone : {}) }}>
            <span style={{ ...S.box, ...(done[i] ? S.boxDone : {}) }}>{done[i] ? "✓" : i + 1}</span>
            <span style={{ ...S.label, textDecoration: done[i] ? "line-through" : "none", opacity: done[i] ? 0.55 : 1 }}>{it}</span>
          </button>
        ))}
        {total === 0 && <div style={S.empty}>Aucune étape configurée. Papa peut les régler dans l'espace parent.</div>}
        <div style={{ height: 30 }} />
      </div>

      {cheer && !allDone && <div style={S.cheer}>{cheer}</div>}

      {allDone && (
        <div style={S.winOverlay} onClick={onClose}>
          <div style={S.winCard}>
            <div style={{ fontSize: 46 }}>🎉</div>
            <div style={{ color: "var(--kid-green)", fontWeight: 800, fontSize: 22 }}>RITUEL TERMINÉ !</div>
            <div style={S.winSub}>Toutes les étapes faites, {moment === "morning" ? "bonne journée" : "bonne nuit"} !</div>
            <button style={S.okBtn} onClick={onClose}>👍 Super</button>
          </div>
        </div>
      )}
    </div>
  );
}

const S: Record<string, React.CSSProperties> = {
  screen: { position: "fixed", inset: 0, zIndex: 62, background: "var(--kid-bg)", display: "flex", flexDirection: "column", color: "var(--kid-text)", fontFamily: "var(--kid-font-sans, system-ui)" },
  header: { display: "flex", alignItems: "center", gap: 12, padding: "16px 16px 8px" },
  close: { width: 40, height: 40, borderRadius: 12, border: "1px solid var(--kid-cyan-40)", background: "transparent", color: "var(--kid-cyan)", fontSize: 18, cursor: "pointer" },
  title: { flex: 1, fontFamily: "var(--kid-font-mono)", letterSpacing: "0.1em", fontSize: 15, color: "var(--kid-cyan)" },
  count: { fontFamily: "var(--kid-font-mono)", fontSize: 15, color: "var(--kid-text-2)" },
  barTrack: { height: 6, background: "var(--kid-cyan-10)", margin: "4px 16px 0", borderRadius: 3, overflow: "hidden" },
  barFill: { height: "100%", background: "var(--kid-green)", transition: "width 0.4s ease" },
  scroll: { flex: 1, overflowY: "auto", padding: "18px 16px 0" },
  item: { width: "100%", display: "flex", alignItems: "center", gap: 14, textAlign: "left", padding: "16px", marginBottom: 10, borderRadius: 16, border: "1.5px solid var(--kid-cyan-40)", background: "rgba(255,255,255,0.03)", cursor: "pointer", color: "var(--kid-text)" },
  itemDone: { borderColor: "var(--kid-green)", background: "var(--kid-green-10)" },
  box: { flex: "0 0 auto", width: 32, height: 32, borderRadius: 10, border: "1.5px solid var(--kid-cyan-40)", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "var(--kid-font-mono)", fontSize: 15, color: "var(--kid-cyan)" },
  boxDone: { borderColor: "var(--kid-green)", background: "var(--kid-green)", color: "#04120c", fontWeight: 800 },
  label: { fontSize: 18, lineHeight: 1.3 },
  empty: { color: "var(--kid-text-3)", fontSize: 14, textAlign: "center", padding: 24 },
  cheer: { position: "absolute", bottom: "18%", left: "50%", transform: "translateX(-50%)", background: "var(--kid-green)", color: "#04120c", fontWeight: 800, fontSize: 18, padding: "10px 22px", borderRadius: 999, boxShadow: "0 0 24px var(--kid-green-10)" },
  winOverlay: { position: "fixed", inset: 0, zIndex: 64, background: "rgba(3,5,9,0.82)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 },
  winCard: { textAlign: "center", padding: "28px 32px", borderRadius: 20, border: "1px solid var(--kid-green)", background: "var(--kid-bg)", maxWidth: 340, width: "100%" },
  winSub: { color: "var(--kid-text-3)", fontSize: 14, marginTop: 6 },
  okBtn: { display: "block", width: "100%", marginTop: 20, padding: "14px", borderRadius: 14, border: "none", background: "linear-gradient(180deg, var(--kid-green), #1fbf7a)", color: "#04120c", fontSize: 16, fontWeight: 800, cursor: "pointer" },
};
